/**
 * recent-searches.ts — client-side recent-search history for the marketplace
 * header search bar (STORY-003-008).
 *
 * Persisted in `localStorage` under `marketplace.recent_searches` (story
 * Implementation logic "Recent searches"). This is NOT auth-sensitive data and
 * is explicitly NOT a JWT — `localStorage` is acceptable here per
 * `05-GUARDRAILS.md` (the ONLY thing forbidden from localStorage is the JWT /
 * refresh token). Never store tokens through this module.
 *
 * Each entry is `{ q: string; when: number }`; the list is capped at
 * `MAX_RECENT_SEARCHES` (10) and de-duplicated case-insensitively so a repeated
 * search bubbles to the top instead of stacking. The header dropdown shows the
 * top `RECENT_DROPDOWN_LIMIT` (5).
 *
 * All access is defensive: a malformed / unavailable `localStorage` (private
 * mode, quota, SSR) degrades to an empty list rather than throwing — the search
 * bar must work even when persistence fails.
 */

/** Storage key (story Implementation logic). */
export const RECENT_SEARCHES_KEY = "marketplace.recent_searches";

/** Max stored entries (story: "max 10 entries"). */
export const MAX_RECENT_SEARCHES = 10;

/** Max entries shown in the header typeahead dropdown (story: "top 5"). */
export const RECENT_DROPDOWN_LIMIT = 5;

/** One persisted recent-search entry. */
export interface RecentSearch {
  /** The raw query string the user submitted. */
  q: string;
  /** Epoch milliseconds when it was last submitted (most-recent-first sort). */
  when: number;
}

/** Type guard for a persisted entry (defends against hand-edited storage). */
function isRecentSearch(value: unknown): value is RecentSearch {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate["q"] === "string" &&
    candidate["q"].length > 0 &&
    typeof candidate["when"] === "number"
  );
}

/** Resolve the storage backend, or null when unavailable (SSR / private mode). */
function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    // Accessing `localStorage` can throw in some sandboxed contexts.
    return null;
  }
}

/**
 * Read the full recent-search list (most-recent-first). Returns `[]` on any
 * read / parse failure — never throws.
 */
export function getRecentSearches(): RecentSearch[] {
  const storage = safeStorage();
  if (storage === null) return [];
  let raw: string | null;
  try {
    raw = storage.getItem(RECENT_SEARCHES_KEY);
  } catch {
    return [];
  }
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentSearch).slice(0, MAX_RECENT_SEARCHES);
  } catch {
    return [];
  }
}

/**
 * Record a search query at the top of the list. De-duplicates the query
 * case-insensitively (a repeated search moves to the top with a fresh
 * timestamp), trims, ignores empty queries, and caps the list at
 * `MAX_RECENT_SEARCHES`. Returns the new list (also persisted). Never throws —
 * a persistence failure still returns the would-be list so callers can update
 * in-memory state.
 */
export function addRecentSearch(
  query: string,
  now: number = Date.now(),
): RecentSearch[] {
  const trimmed = query.trim();
  if (trimmed.length === 0) return getRecentSearches();

  const existing = getRecentSearches();
  const folded = trimmed.toLocaleLowerCase();
  const deduped = existing.filter(
    (entry) => entry.q.trim().toLocaleLowerCase() !== folded,
  );
  const next: RecentSearch[] = [{ q: trimmed, when: now }, ...deduped].slice(
    0,
    MAX_RECENT_SEARCHES,
  );

  const storage = safeStorage();
  if (storage !== null) {
    try {
      storage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
    } catch {
      // Quota / private-mode write failure — fall through with the in-memory
      // list so the UI still reflects the submission for this session.
    }
  }
  return next;
}

/**
 * Clear the recent-search history (story: "Cleared on logout"). Never throws.
 */
export function clearRecentSearches(): void {
  const storage = safeStorage();
  if (storage === null) return;
  try {
    storage.removeItem(RECENT_SEARCHES_KEY);
  } catch {
    // Ignore — nothing to clean up if removal fails.
  }
}
